import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { storage } from '../../services/StorageService';
import { displayedPlayerGeometry } from './playerWindowGeometry';

/**
 * One stack for every window: the window opened, restored or dragged last takes the next level, so
 * it is on top whatever order the host renders windows in. Levels start at 1000 (where a drag has
 * always raised a window); a window whose own z-index is higher (a GM layer) keeps it.
 */
let stackTop = 1000;
function nextLevel(own: number): number {
  stackTop += 1;
  return Math.max(own, stackTop);
}

export interface ToolWindowState {
  x: number;
  y: number;
  width: number;
  height: number;
  collapsed: boolean;
  minimized: boolean;
}

export interface ToolWindowHandle {
  getState: () => ToolWindowState;
  setPosition: (x: number, y: number) => void;
  setSize: (width: number, height: number) => void;
  setGeometry: (geometry: Partial<Pick<ToolWindowState, 'x' | 'y' | 'width' | 'height'>>) => void;
  resetGeometry: () => void;
  minimize: () => void;
  restore: () => void;
}

export interface UseWindowGeometryOptions {
  id: string;
  defaultPosition?: { x: number; y: number };
  defaultSize?: { width: number; height: number };
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
  persistState?: boolean;
  restoreOnOpen?: boolean;
  isPlayerWindow?: boolean;
  bringToFrontOnDrag?: boolean;
  initialZIndex?: number;
  onPositionChange?: (x: number, y: number) => void;
  onSizeChange?: (width: number, height: number) => void;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const DEFAULT_MIN_SIZE = { width: 200, height: 100 };
const DEFAULT_MAX_SIZE = { width: 1200, height: 900 };

export function useWindowGeometry(options: UseWindowGeometryOptions) {
  const {
    id,
    defaultPosition,
    defaultSize = { width: 400, height: 500 },
    minSize = DEFAULT_MIN_SIZE,
    maxSize = DEFAULT_MAX_SIZE,
    persistState = true,
    restoreOnOpen = false,
    isPlayerWindow = false,
    bringToFrontOnDrag = true,
    initialZIndex = 100,
    onPositionChange,
    onSizeChange,
    onCollapsedChange,
  } = options;

  const defaultState = useCallback((): ToolWindowState => ({
    x: defaultPosition?.x ?? Math.floor((window.innerWidth - defaultSize.width) / 2),
    y: defaultPosition?.y ?? 50,
    width: defaultSize.width,
    height: defaultSize.height,
    collapsed: false,
    minimized: false,
  }), [defaultPosition, defaultSize]);

  // Load persisted state or use defaults
  const loadState = useCallback((): ToolWindowState => {
    const saved = persistState
      ? storage.getObjectRaw<ToolWindowState>(`toolwindow_${id}`)
      : null;
    if (saved) {
      return {
        ...saved,
        // Ensure within viewport bounds
        x: Math.max(0, Math.min(saved.x, window.innerWidth - 100)),
        y: Math.max(0, Math.min(saved.y, window.innerHeight - 50)),
        width: Math.max(minSize.width, Math.min(saved.width, maxSize.width)),
        height: Math.max(minSize.height, Math.min(saved.height, maxSize.height)),
        collapsed: restoreOnOpen ? false : saved.collapsed,
        minimized: restoreOnOpen ? false : saved.minimized,
      };
    }
    return defaultState();
  }, [id, defaultState, minSize, maxSize, persistState, restoreOnOpen]);

  const [state, setState] = useState<ToolWindowState>(loadState);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [zIndex, setZIndex] = useState(() => (bringToFrontOnDrag ? nextLevel(initialZIndex) : initialZIndex));
  const raise = useCallback(() => setZIndex(nextLevel(initialZIndex)), [initialZIndex]);
  const windowRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const startStateRef = useRef(state);

  // Persist state changes
  useEffect(() => {
    if (persistState) storage.setObjectRaw(`toolwindow_${id}`, state);
  }, [id, persistState, state]);

  const clampGeometry = useCallback((next: ToolWindowState): ToolWindowState => {
    const width = Math.max(minSize.width, Math.min(next.width, maxSize.width));
    const height = Math.max(minSize.height, Math.min(next.height, maxSize.height));
    // Keep at least a title-bar-sized grab target visible on every edge.
    const x = Math.max(-width + 100, Math.min(next.x, window.innerWidth - 100));
    const y = Math.max(0, Math.min(next.y, window.innerHeight - 40));
    return { ...next, x, y, width, height };
  }, [maxSize.height, maxSize.width, minSize.height, minSize.width]);

  // Handle dragging & resizing
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;

        const next = clampGeometry({
          ...startStateRef.current,
          x: startStateRef.current.x + dx,
          y: startStateRef.current.y + dy,
        });

        setState(prev => ({ ...prev, x: next.x, y: next.y }));
        onPositionChange?.(next.x, next.y);
      } else if (isResizing) {
        const dx = e.clientX - dragStartRef.current.x;
        const dy = e.clientY - dragStartRef.current.y;

        const newWidth = Math.max(minSize.width, Math.min(maxSize.width, startStateRef.current.width + dx));
        const newHeight = Math.max(minSize.height, Math.min(maxSize.height, startStateRef.current.height + dy));

        setState(prev => ({
          ...prev,
          width: newWidth,
          height: newHeight,
          ...(isPlayerWindow ? { x: startStateRef.current.x, y: startStateRef.current.y } : {}),
        }));
        onSizeChange?.(newWidth, newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [clampGeometry, isDragging, isPlayerWindow, isResizing, minSize, maxSize, onPositionChange, onSizeChange]);

  const startDrag = (e: React.MouseEvent) => {
    // Don't drag if clicking buttons
    if ((e.target as HTMLElement).closest('.toolwindow-button, button, a, input, select, textarea, [data-interactive]')) return;

    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    startStateRef.current = {
      ...state,
      ...(isPlayerWindow ? displayedPlayerGeometry(windowRef.current) : {}),
    };

    if (bringToFrontOnDrag) raise();
  };

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    startStateRef.current = {
      ...state,
      ...(isPlayerWindow ? displayedPlayerGeometry(windowRef.current) : {}),
    };
  };

  const toggleCollapse = () => {
    setState((prev: ToolWindowState) => {
      const newCollapsed = !prev.collapsed;
      onCollapsedChange?.(newCollapsed);
      return { ...prev, collapsed: newCollapsed };
    });
  };

  const toggleMinimize = () => {
    setState((prev: ToolWindowState) => ({ ...prev, minimized: !prev.minimized }));
  };

  const handle: ToolWindowHandle = {
    getState: () => state,
    setPosition: (x, y) => {
      setState(prev => clampGeometry({ ...prev, x, y }));
    },
    setSize: (width, height) => {
      setState(prev => clampGeometry({ ...prev, width, height }));
    },
    setGeometry: (geometry) => {
      setState(prev => clampGeometry({ ...prev, ...geometry }));
    },
    resetGeometry: () => {
      setState(clampGeometry(defaultState()));
    },
    minimize: () => {
      setState(prev => ({ ...prev, minimized: true }));
    },
    restore: () => {
      setState(prev => ({ ...prev, minimized: false, collapsed: false }));
      if (bringToFrontOnDrag) raise();
    },
  };

  // Where a reset would put the window: its default place and size (collapse state aside).
  const fresh = defaultState();
  const isDefaultGeometry = state.x === fresh.x && state.y === fresh.y && state.width === fresh.width && state.height === fresh.height;

  return {
    state,
    isDefaultGeometry,
    setState,
    isDragging,
    isResizing,
    zIndex,
    setZIndex,
    windowRef,
    startDrag,
    startResize,
    toggleCollapse,
    toggleMinimize,
    handle,
    minSize,
    maxSize,
  };
}
